<template>
	<v-row class="full-height bin-utility-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="bin-utility-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchForm.keywords"
								placeholder="Search style codes..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="bin-utility-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<!-- Missing Bins Filter -->
						<v-col cols="auto">
							<v-checkbox
								v-model="searchForm.filterMissingBin"
								label="Missing Bins Only"
								hide-details
								class="mt-0 pt-0"
								@change="onFilterChange"
							></v-checkbox>
						</v-col>

						<!-- Spacer -->
						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Near-restock buffer (global) -->
						<v-col cols="auto" class="d-flex align-center" style="gap: 0.6rem">
							<span style="font-size: 1.3rem; white-space: nowrap; opacity: 0.75"
								>Restock alert within</span
							>
							<v-text-field
								v-model.number="nearRestockBuffer"
								type="number"
								min="0"
								outlined
								dense
								hide-details
								style="max-width: 7rem"
								:disabled="!canUpdateBin()"
								@input="onBufferInput"
							></v-text-field>
							<span style="font-size: 1.3rem; white-space: nowrap; opacity: 0.75">units</span>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 bin-utility-table-container">
			<v-data-table
				:headers="headers"
				:items="inventoryItems"
				:loading="loading"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="bin-utility-data-table"
				must-sort
				fixed-header
			>
				<!-- Style Code Column -->
				<template v-slot:item.styleCode="{ item }">
					{{ item.styleCode }}
				</template>

				<!-- Description Column -->
				<template v-slot:item.description="{ item }">
					{{ item.description }}
				</template>

				<!-- Has Sizes Column -->
				<template v-slot:item.hasSizes="{ item }">
					{{ renderStockedSizes(item) }}
				</template>

				<!-- Infinite Inventory Column -->
				<template v-slot:item.infiniteInventory="{ item }">
					{{ item.infiniteInventory ? "Yes" : "No" }}
				</template>

				<!-- On Hand Column -->
				<template v-slot:item.onHand="{ item }">
					<v-icon v-if="item.infiniteInventory">mdi-infinity</v-icon>
					<span v-else>{{ item.onHand }}</span>
				</template>

				<!-- Committed Column -->
				<template v-slot:item.committed="{ item }">
					{{ item.committed }}
				</template>

				<!-- Action Column -->
				<template v-slot:item.action="{ item }">
					<v-btn text color="primary" @click.stop="navigateToItem(item)">
						Stock Sizes
						<v-icon style="font-size: 1.4rem">mdi-arrow-right</v-icon>
					</v-btn>
				</template>

				<!-- Loading slot -->
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<!-- No data slot -->
				<template v-slot:no-data>
					<div class="text-center pa-4">
						<v-icon large color="grey lighten-1">mdi-package-variant</v-icon>
						<p class="mt-2 grey--text">No stocked style codes found</p>
					</div>
				</template>
			</v-data-table>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import { hasPermission, SystemPermissions } from "@/models/User";
import { getApiUrl } from "@/helpers";
import { GET_INVENTORY_SETTINGS } from "@/gql/inventory";

export default {
	name: "BinUtilityList",
	data() {
		return {
			loading: true,
			searchForm: {
				keywords: "",
				filterMissingBin: false,
			},
			nearRestockBuffer: 5,
			restockSaveTimeout: null,
			inventoryItems: [],
			totalCount: 0,
			searchTimeout: null,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: ["styleCode"],
				sortDesc: [false],
			},
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50],
				"show-first-last-page": true,
			},
			headers: [
				{
					text: "Style Code",
					value: "styleCode",
					sortable: true,
					width: "12%",
				},
				{
					text: "Description",
					value: "description",
					sortable: true,
					width: "28%",
				},
				{
					text: "Has Sizes",
					value: "hasSizes",
					sortable: true,
					width: "12%",
					align: "center",
				},
				{
					text: "Infinite Inventory",
					value: "infiniteInventory",
					sortable: true,
					width: "14%",
					align: "center",
				},
				{
					text: "Total On Hand",
					value: "onHand",
					sortable: true,
					width: "12%",
					align: "center",
				},
				{
					text: "Total Committed",
					value: "committed",
					sortable: true,
					width: "12%",
					align: "center",
				},
				{
					text: "",
					value: "action",
					sortable: false,
					width: "10%",
					align: "end",
				},
			],
		};
	},
	computed: {
		navigationDirection() {
			return this.$store.getters["globalState/navigationDirection"];
		},
	},
	watch: {
		tableOptions: {
			handler() {
				this.fetchData();
			},
			deep: true,
		},
		navigationDirection: {
			handler(newDirection) {
				if (newDirection) {
					const savedState = this.$store.getters["globalState/binUtilityState"];
					if (savedState) {
						this.tableOptions.page = savedState.offset / 15 + 1;
						this.searchForm.keywords = savedState.keywords || "";
						this.searchForm.filterMissingBin = savedState.filterMissingBin || false;
						if (savedState.sortBy) {
							this.tableOptions.sortBy = [savedState.sortBy];
						}
						if (savedState.sortDesc !== undefined) {
							this.tableOptions.sortDesc = [savedState.sortDesc];
						}
					}
				}
			},
			immediate: true,
		},
	},
	methods: {
		canUpdateBin() {
			const user = this.$store.getters.userInfo;
			if (hasPermission(user, SystemPermissions.Inventory_BinUtility_Update)) {
				return true;
			}
			return user.isAdmin;
		},

		navigateToItem(item) {
			this.$router.push(`/inventory/bin-utility/${item.inventoryItemId}`);
		},

		renderStockedSizes(item) {
			return item.hasSizes ? "Yes" : "No";
		},

		// Map table column values to GraphQL sort field names
		getSortFieldName(columnValue) {
			const sortFieldMap = {
				systemCategoryId: "systemCategoryId",
				styleCode: "styleCode",
				description: "description",
				hasSizes: "hasSizes",
				infiniteInventory: "infiniteInventory",
				onHand: "onHand",
				committed: "committed",
			};
			return sortFieldMap[columnValue] || "styleCode";
		},

		buildQueryVariables() {
			const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
			const keyword = (this.searchForm.keywords || "").trim();

			// Build where clause
			const where = {
				and: [
					{ stockItems: { eq: true } },
					{ inactiveDate: { eq: null } },
				],
			};

			// Add missing bins filter
			if (this.searchForm.filterMissingBin) {
				where.and.push({ missingBins: { eq: true } });
			}

			// Build dynamic sort order
			const sortColumn = sortBy[0] || "systemCategoryId";
			const sortDirection = sortDesc[0] ? "DESC" : "ASC";
			const sortFieldName = this.getSortFieldName(sortColumn);
			const order = [{ [sortFieldName]: sortDirection }];

			return {
				offset: (page - 1) * itemsPerPage,
				take: itemsPerPage,
				order,
				searchTerm: keyword || null,
				where,
			};
		},

		async fetchData() {
			this.loading = true;
			try {
				const variables = this.buildQueryVariables();
				const res = await this.$apollo.query({
					query: gql`
						query GetInventoryListings(
							$offset: Int!
							$take: Int!
							$searchTerm: String
							$order: [InventoryItemListingSortInput!] = [{ styleCode: ASC }]
							$where: InventoryItemListingFilterInput = null
						) {
							inventoryListings(
								skip: $offset
								take: $take
								order: $order
								searchTerm: $searchTerm
								where: $where
							) {
								totalCount
								items {
									inventoryItemId
									systemCategoryId
									styleCode
									description
									infiniteInventory
									stockItems
									hasSizes
									onHand
									committed
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables,
					fetchPolicy: "no-cache",
				});

				this.totalCount = res.data.inventoryListings.totalCount;
				this.inventoryItems = res.data.inventoryListings.items;
				this.loading = false;

				// Auto-navigate if exactly one result is found from a search with exact match
				if (
					this.searchForm.keywords &&
					this.searchForm.keywords.trim() !== "" &&
					this.inventoryItems.length === 1 &&
					(this.searchForm.keywords === this.inventoryItems[0]?.styleCode ||
						this.searchForm.keywords === this.inventoryItems[0]?.description)
				) {
					this.navigateToItem(this.inventoryItems[0]);
				}
			} catch (error) {
				console.error("Error fetching bin utility items:", error);
				this.loading = false;
			}
		},

		onSearchInput() {
			// Debounced search
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
				this.fetchData();
			}, 400);
		},

		onSearchClear() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}
			this.tableOptions.page = 1;
			this.fetchData();
		},

		onFilterChange() {
			this.tableOptions.page = 1;
			this.fetchData();
		},

		async loadRestockBuffer() {
			try {
				const res = await this.$apollo.query({
					query: GET_INVENTORY_SETTINGS,
					fetchPolicy: "no-cache",
				});
				const buffer = res.data?.inventorySettings?.nearRestockBuffer;
				if (typeof buffer === "number") {
					this.nearRestockBuffer = buffer;
				}
			} catch (error) {
				console.error("Error loading inventory settings:", error);
			}
		},

		onBufferInput() {
			if (!this.canUpdateBin()) return;
			if (this.restockSaveTimeout) clearTimeout(this.restockSaveTimeout);
			this.restockSaveTimeout = setTimeout(() => this.saveBuffer(), 600);
		},

		async saveBuffer() {
			const value = parseInt(this.nearRestockBuffer);
			if (isNaN(value) || value < 0) return;
			try {
				await this.$http.put(getApiUrl("/api/settings/inventory-settings"), {
					nearRestockBuffer: value,
				});
			} catch (error) {
				console.error("Error saving near-restock buffer:", error);
			}
		},
	},
	mounted() {
		// Initial fetch will happen via tableOptions watcher
		this.loadRestockBuffer();
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
		if (this.restockSaveTimeout) {
			clearTimeout(this.restockSaveTimeout);
		}
	},
	beforeRouteLeave(to, from, next) {
		this.$store.dispatch("globalState/saveBinUtilityState", {
			offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
			keywords: this.searchForm.keywords,
			filterMissingBin: this.searchForm.filterMissingBin,
			sortBy: this.tableOptions.sortBy[0],
			sortDesc: this.tableOptions.sortDesc[0],
			scrollPosition: window.scrollY,
		});
		next();
	},
};
</script>

<style lang="scss" scoped>
.bin-utility-page {
	// Use calc to fit within viewport: 100vh - header (64px) - container padding (24px)
	height: calc(100vh - 88px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.bin-utility-table-container {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;
}

.bin-utility-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	flex-shrink: 0; // Don't shrink the toolbar
}

.bin-utility-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

.bin-utility-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0; // Critical for flex children to respect overflow
	overflow: hidden;

	// Table wrapper should scroll
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;

		table {
			min-width: 900px;
		}
	}

	// Keep footer fixed at bottom
	::v-deep .v-data-footer {
		flex-shrink: 0;
	}

	// Match existing header styling
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.4rem !important;
		font-weight: 500 !important;
		color: #000 !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
	}

	// Match existing row height and font size
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
	}

	// Footer styling
	::v-deep .v-data-footer {
		font-size: 1.25rem !important;
	}

	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
}
</style>
